/** View-only projection of a complete, stored email. The original string is never rewritten.
 * Presentation-table scaffolding is traversed using exact source spans. Recognized content
 * uses the existing source adapter; other content stays protected HTML. This is not a renderer:
 * tokens and every Liquid branch remain literal here; the separate server preview resolves them.
 */
import { canvasSource, waypointSource, stylePatchApi, neutral, type EbDoc, type EbNode, type EbOp, type EbStyle } from "@v0y4ge/jrni-email-builder";
import { sanitizeEmailDocumentHtml } from "@v0y4ge/html-safe";

export interface HtmlSection extends EbNode { start: number; end: number }
export interface HtmlSourceView { nodes: Record<string, HtmlSection>; doc: EbDoc; rootIds: string[]; canvasHtml: string }
export type HtmlStyleEdit = { ok: true; html: string } | { ok: false; reason: string };
const WRAPPERS = new Set(["html", "body", "table", "tbody", "thead", "tfoot", "tr", "td", "th", "div", "section"]);
const INLINE = new Set(["a", "span", "strong", "em", "b", "i", "u", "small", "br"]);

export function projectHtmlSource(source: string): HtmlSourceView {
  const nodes: Record<string, HtmlSection> = {}, doc: EbDoc = {}, rootIds: string[] = [];
  const marks: { at: number; end: number; id: string }[] = [];
  const top = canvasSource.parse(source);
  if (!top.ok) throw new Error(top.reason);
  let from = 0, to = source.length;
  const html = top.seq.find((n) => n.kind === "el" && n.tag === "html");
  if (html?.kind === "el") {
    const children = canvasSource.parse(source.slice(html.openEnd, html.closeStart));
    if (!children.ok) throw new Error(children.reason);
    const body = children.seq.find((n) => n.kind === "el" && n.tag === "body");
    if (!body || body.kind !== "el") throw new Error("The email document has no readable body.");
    from = html.openEnd + body.openEnd; to = html.openEnd + body.closeStart;
  }
  function walk(start: number, end: number, depth: number): void {
    if (depth > 48) throw new Error("This email's HTML is nested too deeply to inspect visually.");
    const fragment = source.slice(start, end), p = waypointSource.project(fragment);
    if (!p.ok) throw new Error(p.reason);
    for (const localId of p.rootIds) {
      const m = p.nodes[localId];
      if (!m || m.nested) continue;
      const el = p.tree.seq.find((n) => n.kind === "el" && n.start + p.off === m.start);
      if (m.mode === "source" && el?.kind === "el" && !el.isVoid && WRAPPERS.has(el.tag) && !/\sdata-block(?:-kind)?\s*=/.test(el.open)) {
        const child = canvasSource.parse(p.inner.slice(el.openEnd, el.closeStart));
        if (child.ok && child.seq.some((n) => n.kind === "logic" || n.kind === "opaque" || (n.kind === "el" && !INLINE.has(n.tag)))) {
          walk(start + p.off + el.openEnd, start + p.off + el.closeStart, depth + 1); continue;
        }
      }
      const a = start + m.start, b = start + m.end, id = `html-${a}`;
      const block = p.doc[localId];
      // The view lists exact sections, not a second persisted block document. Managed islands
      // remain protected as a whole because their descendant operations are out of scope here.
      const mode = m.mode === "managed" ? "source" : m.mode;
      const attrs = el?.kind === "el" ? waypointSource.tagAttrs(el.open) : null;
      const blockName = attrs?.list.find((x) => x.name === "data-block")?.value;
      const label = blockName ? blockName.replace(/^email_/, "").replace(/_/g, " ") : m.label || m.type;
      const styles = el?.kind === "el" && m.mode === "legacy" ? stylePatchApi.read(el.open) : null;
      const data = styles?.ok ? { ...(m.data || block?.data || {}), style: styles.style } : m.data || block?.data;
      nodes[id] = { id, type: mode === "source" ? "Source" : m.type, mode, label,
        reason: mode === "source" ? "Stored HTML is preserved exactly. View its source to inspect this section." : m.reason,
        start: a, end: b, expect: source.slice(a, b), data,
        parentId: "root", slot: 0, index: rootIds.length, canMoveUp: false, canMoveDown: false, canDelete: false, canDuplicate: false, noInsert: true,
        ...(m.mode === "legacy" && styles?.ok ? { inPlaceStyles: true, styleCustom: styles.custom } : {}) };
      doc[id] = { type: mode === "source" ? "JrniSource" : m.type,
        data: mode === "source" ? { props: { source: source.slice(a, b), label } } : data || {} };
      rootIds.push(id);
      if (el?.kind === "el") marks.push({ at: start + p.off + el.start, end: start + p.off + el.openEnd, id });
    }
  }
  walk(from, to, 0);
  // Neutralize authored editor markers before adding our own. Slice ORIGINAL offsets,
  // since neutralization may change lengths. Neither operation reaches the stored source.
  let canvasHtml = "", pos = 0;
  for (const mark of marks.sort((a, b) => a.at - b.at)) {
    const open = source.slice(mark.at, mark.end);
    if (!/^<[a-zA-Z][\w:-]*/.test(open)) throw new Error("A section's HTML opening tag could not be located.");
    canvasHtml += neutral(source.slice(pos, mark.at)) + neutral(open).replace(/^(<[a-zA-Z][\w:-]*)/, `$1 data-eb="${mark.id}"`);
    pos = mark.end;
  }
  canvasHtml += neutral(source.slice(pos));
  return { nodes, doc, rootIds, canvasHtml };
}

/** Apply only one visual style update when the builder can prove it maps to a
 * single exact opening tag. Structural/content/import operations stay unsupported.
 */
export function applyHtmlStyleEdit(source: string, view: HtmlSourceView, op: EbOp): HtmlStyleEdit {
  const reject = (reason: string): HtmlStyleEdit => ({ ok: false, reason });
  if (op.type !== "update") return reject("This operation cannot be represented safely in stored HTML.");
  const node = view.nodes[op.id];
  if (!node || node.mode !== "legacy" || !node.inPlaceStyles || !node.data || op.expect !== node.expect) return reject("This section changed or does not support source-safe style edits.");
  const withoutStyle = (data: typeof op.data) => { const copy = { ...data }; delete copy.style; return JSON.stringify(copy); };
  if (withoutStyle(op.data) !== withoutStyle(node.data)) return reject("Only supported inline style properties can be changed from the visual panel.");
  const span = source.slice(node.start, node.end);
  if (span !== node.expect) return reject("The selected source span changed. Reopen the visual section before editing it.");
  const parsed = canvasSource.parse(span);
  if (!parsed.ok) return reject("The selected source section is no longer parseable.");
  const opening = parsed.seq.find((part) => part.kind === "el");
  if (!opening || opening.kind !== "el" || opening.start !== 0) return reject("The selected style could not be mapped to one exact opening tag.");
  const read = stylePatchApi.read(opening.open);
  if (!read.ok) return reject("This opening tag's styles cannot be safely edited.");
  const prior = read.style as EbStyle, next = (op.data.style || {}) as EbStyle;
  const patched = stylePatchApi.patch(opening.open, prior, next);
  if (patched == null) return reject("The requested style is unsupported or invalid.");
  if (patched === opening.open) return { ok: true, html: source };
  const nextSpan = patched + span.slice(opening.openEnd), html = source.slice(0, node.start) + nextSpan + source.slice(node.end);
  try { projectHtmlSource(html); } catch { return reject("The style change would make the stored source invalid."); }
  return { ok: true, html };
}

/** Isolation of the canvas display copy. Source and server-rendered output stay separate. */
export function isolateEmailCanvas(html: string): string {
  const d = new DOMParser().parseFromString(sanitizeEmailDocumentHtml(html), "text/html");
  d.querySelectorAll("script,iframe,frame,frameset,object,embed,form,input,button,select,textarea,link,base,meta[http-equiv],audio,video,source,track,portal").forEach((n) => n.remove());
  for (const el of d.querySelectorAll("*")) {
    for (const a of [...el.attributes]) {
      const name = a.name.toLowerCase();
      if (name.startsWith("on") || ["srcset", "action", "formaction", "background", "poster", "xlink:href", "ping"].includes(name)) el.removeAttribute(a.name);
      if (name === "href") { el.setAttribute("data-inert-href", a.value); el.removeAttribute(a.name); }
      if (name === "src" && !/^data:image\/(png|jpeg|webp);base64,/i.test(a.value)) el.removeAttribute(a.name);
    }
  }
  const csp = d.createElement("meta"); csp.httpEquiv = "Content-Security-Policy";
  csp.content = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:; form-action 'none'; base-uri 'none'";
  d.head.prepend(csp);
  return "<!DOCTYPE html>" + d.documentElement.outerHTML;
}
